import './payment-sign-up.scss';
import {useLocation, useNavigate} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {INewUser} from "../../../../../constants/types/INewUser";
import {useSoftToHardApplicant} from "../../../../../api/soft-user/useSoftToHardApplicant";
import {object, string, ValidationError} from "yup";
import Loading from "../../../../../components/UI/Loading/Loading";
import InputKMQ from "../../../../../components/UI/Input/InputKMQ";
import PasswordRequirements from "../../../../../components/login/PasswordRequirements/PasswordRequirements";
import Checkbox from "../../../../../components/UI/Checkbox/Checkbox";
import ButtonKMQ from "../../../../../components/UI/Button/ButtonKMQ";
import ErrorKMQ from "../../../../../components/UI/Error/ErrorKMQ";
import IdentityProviders from "../../../../login/identity_providers";
import {ButtonTypes} from "../../../../../constants/ui/types/ButtonTypes";
import Modal from "react-modal";
import LoginVerification from "../../../../login/verification-code/LoginVerification";
import {XCircle} from "@phosphor-icons/react";

const PaymentSignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [newUser, setNewUser] = useState<INewUser>({});
  const [passwordIsChecked, setPasswordIsChecked] = useState<boolean>(false);
  const [dataIsAccepted, setDataIsAccepted] = useState<boolean>(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState<boolean>(false);
  const [inputError, setInputError] = useState<boolean>(false);
  const {
    mutate: createHardUser,
    data,
    isPending,
    error
  } = useSoftToHardApplicant();

  const signUpSchema = object({
    email: string().min(3).required(),
    password: string().min(3).required(),
    confirmation_password: string().min(3).required(),
    first_name: string().min(1).required(),
    last_name: string().min(1).required(),
    date_of_birth: string().length(10).required(),
  });

  const handleCreateUser = async () => {
    if (newUser.password && newUser.password !== newUser.confirmation_password) {
      setConfirmPasswordError(true);
    } else {
      try {
        await signUpSchema.validate(newUser);
        createHardUser(newUser);
        localStorage.removeItem('basic_info');
        // @ts-ignore
      } catch (e: ValidationError) {
        console.log('e', e.errors)
        setInputError(true);
      }
    }
  }

  useEffect(() => {
    if (confirmPasswordError && newUser.password && newUser.password === newUser.confirmation_password) {
      setConfirmPasswordError(false);
    }
  }, [confirmPasswordError, newUser])

  useEffect(() => {
    if (data && newUser.email) {
      localStorage.setItem('user-email', newUser.email);
      navigate(location.pathname + '?verify=true');
    }
  }, [data])

  useEffect(() => {
    const savedData = localStorage.getItem('basic_info');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setNewUser({
        ...newUser,
        email: parsedData?.email,
        first_name: parsedData?.first_name,
        last_name: parsedData?.last_name,
      })
    }
  }, [])

  return <div className={'payment-sign-up'}>
    <Loading loading={isPending}/>
    <div className={'payment-sign-up-express'}>
      <div className={'payment-sign-up-express-title'}>
        Express login
      </div>

      <IdentityProviders/>
    </div>

    <div className={'payment-sign-up-or'}>
      <div className={'payment-sign-up-or-line'}/>
      <span className={'payment-sign-up-or-text'}>
          Or
      </span>
    </div>


    <div className={'payment-sign-up-content'}>

      <InputKMQ
        value={newUser.first_name ?? ''}
        onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
        type={'text'}
        placeholder={'Enter first name'}
        label={'First name'}
        error={
          (inputError && !newUser.first_name)
            ? 'Please enter your first name'
            : ''
        }
      />

      <InputKMQ
        value={newUser.last_name ?? ''}
        onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
        type={'text'}
        placeholder={'Enter last name'}
        label={'Last name'}
        error={
          (inputError && !newUser.last_name)
            ? 'Please enter your last name'
            : ''
        }
      />

      <InputKMQ
        value={newUser.date_of_birth ?? ''}
        onChange={(e) => {
          if (e.target.value.length === 10) {
            setNewUser({...newUser, date_of_birth: e.target.value}) ;
          }
        }}
        type={'date'}
        placeholder={'Enter date of birth'}
        label={'Date of birth'}
        error={
          (inputError && !newUser.date_of_birth)
            ? 'Please enter your date of birth'
            : ''
        }
      />

      <InputKMQ
        value={newUser.email ?? ''}
        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
        type={'text'}
        placeholder={'Enter email'}
        label={'Email'}
        error={
          (inputError && !newUser.email)
            ? 'Please enter your email'
            // @ts-ignore
            : error?.response?.status === 409
              ? 'This email has already been registered'
              : ''
        }
      />

      <InputKMQ
        value={newUser.password ?? ''}
        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
        type={'password'}
        placeholder={'Enter password'}
        label={'Create password'}
        passwordShowIcon
      />

      <InputKMQ
        value={newUser.confirmation_password ?? ''}
        onChange={(e) => setNewUser({...newUser, confirmation_password: e.target.value})}
        type={'password'}
        placeholder={'Enter password'}
        label={'Confirm password'}
        passwordShowIcon
        error={confirmPasswordError ? 'Passwords do not match' : ''}
      />

      <PasswordRequirements
        password={newUser.password}
        setPasswordCheck={setPasswordIsChecked}
      />

      <Checkbox
        active={dataIsAccepted}
        handleClick={() => setDataIsAccepted(!dataIsAccepted)}
        text={'I acknowledge that by proceeding with payment to KnowMeQ Incorporated for its Credit for Prior Learning tool, that my information (resume, work and education experience) will be shared with specified postsecondary institution and its staff members for credit processing'}
      />

      <div className={'payment-sign-up-continue'}>
        <ButtonKMQ
          onClick={handleCreateUser}
          type={ButtonTypes.Tertiary}
          disabled={!dataIsAccepted || !passwordIsChecked}>
          Verify email
        </ButtonKMQ>
        {
          inputError
            ? <ErrorKMQ
              error={"Some fields are incomplete or data is invalid, please fill in all required information."}/>
            : null
        }
      </div>
    </div>

    <Modal isOpen={location.search.includes('verify')} portalClassName={'verify-email-modal'}>
      <LoginVerification/>
      <span className={'alert-modal-body-close'} onClick={() => navigate(location.pathname)}>
        <XCircle size={28} color="#212121"/>
      </span>
    </Modal>
  </div>
}

export default PaymentSignUp;